/**
 * SpokenSplitter — a stateful streaming parser that splits a token stream into SPOKEN text (inside
 * `<spoken>…</spoken>`) and DETAIL text (everything outside). Used by the worker delivery path: the
 * capable worker wraps what the user should HEAR in `<spoken>` tags; detail prose is the work record.
 *
 * Buffers partial tags split across chunk boundaries ("<spo" + "ken>") so a tag is never mis-emitted
 * as detail. Pure class — no IoC ceremony.
 */
const OPEN = '<spoken>';
const CLOSE = '</spoken>';

/**
 * SentenceCoalescer — buffers streamed spoken fragments and emits COMPLETE sentences. A worker on a
 * fragmenting model (cursor streams word-by-word deltas) would otherwise yield one utterance per word —
 * choppy TTS (a begin→end turn each) and one screen-echo line per word. feed() returns the sentence(s)
 * completed by this chunk (or ''); flush() returns the trailing partial when the stream ends.
 */
const CLOSERS = `"')]}»”’`; // closing quotes/brackets that hug a sentence-ender ("…sponge?")

/** A speakable chunk must contain at least one letter or digit — a punctuation/quote-only chunk (e.g. a
 *  lone ") produces no audio and STALLS the TTS (endSpeech waits for a 'done' that never arrives). */
const hasSpeech = (s: string): boolean => /[\p{L}\p{N}]/u.test(s);

export class SentenceCoalescer {
  private buf = '';
  private static isEnd(c: string): boolean { return c === '\n' || c === '.' || c === '!' || c === '?' || c === '…'; }

  feed(delta: string): string {
    if (delta) this.buf += delta;
    let cut = -1;
    for (let i = 0; i < this.buf.length; i++) if (SentenceCoalescer.isEnd(this.buf[i])) cut = i;
    if (cut < 0) return ''; // no sentence boundary yet — hold for more deltas
    // Absorb a closing quote/bracket sitting right after sentence-ending PUNCTUATION ("…sponge?") into the
    // same chunk — otherwise the closer is orphaned onto the next utterance as a lone, unspeakable " that
    // stalls TTS. Skip for a newline cut (a quote after \n is an OPENING quote for the next line).
    if (this.buf[cut] !== '\n') while (cut + 1 < this.buf.length && CLOSERS.includes(this.buf[cut + 1])) cut++;
    const ready = this.buf.slice(0, cut + 1).trim();
    this.buf = this.buf.slice(cut + 1);
    return hasSpeech(ready) ? ready : ''; // drop a word-less (punctuation/quote-only) chunk — never speak it
  }

  flush(): string { const s = this.buf.trim(); this.buf = ''; return hasSpeech(s) ? s : ''; }
}


export class SpokenSplitter {
  private buf = '';
  private inSpoken = false;
  /** True once any spoken char has ever been emitted (drives the no-spoken fallback). */
  public spokeAny = false;

  /** Feed a delta; returns the spoken/detail spans completed by this chunk (either may be ''). */
  feed(delta: string): { spoken: string; detail: string } {
    this.buf += delta;
    return this.drain(false);
  }

  /** Drain any buffered partial. A trailing `<…` that never completed a tag is emitted as detail. */
  flush(): { spoken: string; detail: string } {
    return this.drain(true);
  }

  private drain(final: boolean): { spoken: string; detail: string } {
    let spoken = '';
    let detail = '';
    while (this.buf.length) {
      const tag = this.inSpoken ? CLOSE : OPEN;
      const idx = this.buf.indexOf(tag);
      if (idx >= 0) {
        // Emit text up to the tag, then consume the tag and flip mode.
        const text = this.buf.slice(0, idx);
        if (this.inSpoken) spoken += text; else detail += text;
        this.buf = this.buf.slice(idx + tag.length);
        this.inSpoken = !this.inSpoken;
        continue;
      }
      // No complete tag in the buffer. Hold back a trailing `<…` run that is a proper prefix of the
      // tag we're currently seeking (it could complete on the next feed). Only the ACTIVE tag matters.
      const lt = this.buf.lastIndexOf('<');
      const holdStart = lt >= 0 && tag.startsWith(this.buf.slice(lt)) ? lt : this.buf.length;
      const text = this.buf.slice(0, holdStart);
      if (this.inSpoken) spoken += text; else detail += text;
      this.buf = this.buf.slice(holdStart);
      break;
    }
    if (final && this.buf) {
      // A dangling partial tag that never completed — surface it as detail (never lost).
      if (this.inSpoken) spoken += this.buf; else detail += this.buf;
      this.buf = '';
    }
    // Gate on non-BLANK content: `<spoken>   </spoken>` would flip spokeAny true, but downstream
    // speak/enqueueUtterance trims it to nothing → silent turn AND a suppressed gist fallback.
    if (spoken.trim()) this.spokeAny = true;
    return { spoken, detail };
  }
}
